/**
 * Camera input chassis. getUserMedia + capture-to-blob.
 *
 * v0: captures a single frame for document upload preview. OCR is
 * intentionally NOT bundled here; downstream handler decides whether to
 * call Tesseract.js (lazy) or Adobe PDF MCP (via owner's host loop).
 */

export interface CaptureResult {
  blob: Blob;
  width: number;
  height: number;
  mime: string;
}

export async function capturePhoto(maxWidth = 1600): Promise<CaptureResult | null> {
  if (!navigator.mediaDevices?.getUserMedia) return null;
  let stream: MediaStream | null = null;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment', width: { ideal: maxWidth }, height: { ideal: Math.round(maxWidth * 4 / 3) } },
      audio: false,
    });
    const video = document.createElement('video');
    video.srcObject = stream;
    video.playsInline = true;
    await video.play();
    await new Promise((r) => setTimeout(r, 250));
    const w = video.videoWidth || maxWidth;
    const h = video.videoHeight || Math.round(maxWidth * 4 / 3);
    const canvas = new OffscreenCanvas(w, h);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, w, h);
    const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality: 0.85 });
    return { blob, width: w, height: h, mime: 'image/jpeg' };
  } catch {
    return null;
  } finally {
    if (stream) stream.getTracks().forEach((t) => t.stop());
  }
}

export function isCameraSupported(): boolean {
  return Boolean(navigator.mediaDevices?.getUserMedia);
}
